import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function PersonalDetailsSection({ profile }) {
  const queryClient = useQueryClient();
  const personal = profile.personal_details || {
    gender: "male",
    marital_status: "Single / unmarried",
    date_of_birth: "",
    category: "",
    differently_abled: false,
    career_break: false,
    permanent_address: "",
    hometown: "",
    pincode: "",
    work_permit: "",
    languages: [],
  };

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(personal);

  const updateMutation = useMutation({
    mutationFn: (data) => backendApi.entities.UserProfile.updatePersonal(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Personal details</h3>

        <button
          onClick={() => setIsEditing(true)}
          className="text-blue-600 hover:underline text-sm font-medium"
        >
          {personal.date_of_birth ? "Edit" : "Add"}
        </button>
      </div>

      {/* Display Values */}
      <div className="grid grid-cols-2 gap-y-6 gap-x-12">
        <div>
          <p className="text-sm text-gray-500">Personal</p>
          <p className="text-gray-800">
            {personal.gender}, {personal.marital_status}
            {!personal.date_of_birth && (
              <span className="text-blue-600 ml-1">Add more info</span>
            )}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Work permit</p>
          <p className={personal.work_permit ? "text-gray-800" : "text-blue-600"}>
            {personal.work_permit || "Add work permit"}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Permanent Address</p>
          <p className="text-gray-800">
            {personal.permanent_address || "Not added"}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Hometown</p>
          <p className="text-gray-800">{personal.hometown || "Not added"}</p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Pincode</p>
          <p className="text-gray-800">{personal.pincode || "Not added"}</p>
        </div>

        <div className="col-span-2 mt-2">
          <p className="text-sm text-gray-500">Languages</p>
          <p className="text-gray-800">
            {personal.languages?.length > 0
              ? personal.languages.map(l => l.name).join(", ")
              : "Not added"}
          </p>
        </div>
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Personal Details</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Gender */}
            <div>
              <Label>Gender</Label>
              <Select
                value={formData.gender}
                onValueChange={(value) =>
                  setFormData({ ...formData, gender: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Marital Status */}
            <div>
              <Label>Marital Status</Label>
              <Select
                value={formData.marital_status}
                onValueChange={(value) =>
                  setFormData({ ...formData, marital_status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Single / unmarried">
                    Single / Unmarried
                  </SelectItem>
                  <SelectItem value="Married">Married</SelectItem>
                  <SelectItem value="Widowed">Widowed</SelectItem>
                  <SelectItem value="Divorced">Divorced</SelectItem>
                  <SelectItem value="Separated">Separated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* DOB */}
            <div>
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) =>
                  setFormData({ ...formData, date_of_birth: e.target.value })
                }
              />
            </div>

            {/* Category */}
            <div>
              <Label>Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData({ ...formData, category: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General</SelectItem>
                  <SelectItem value="SC">SC</SelectItem>
                  <SelectItem value="ST">ST</SelectItem>
                  <SelectItem value="OBC">OBC</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Address */}
            <div>
              <Label>Permanent Address</Label>
              <Input
                value={formData.permanent_address}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    permanent_address: e.target.value,
                  })
                }
              />
            </div>

            {/* Hometown */}
            <div>
              <Label>Hometown</Label>
              <Input
                value={formData.hometown}
                onChange={(e) =>
                  setFormData({ ...formData, hometown: e.target.value })
                }
              />
            </div>

            {/* Pincode */}
            <div>
              <Label>Pincode</Label>
              <Input
                value={formData.pincode}
                onChange={(e) =>
                  setFormData({ ...formData, pincode: e.target.value })
                }
              />
            </div>

            {/* Work Permit */}
            <div>
              <Label>Work Permit</Label>
              <Input
                value={formData.work_permit}
                onChange={(e) =>
                  setFormData({ ...formData, work_permit: e.target.value })
                }
                placeholder="e.g. USA, UK, Canada"
              />
            </div>

            {/* Languages */}
            <div>
              <Label>Languages (Comma separated)</Label>
              <Input
                value={(formData.languages || []).map((l) => l.name).join(", ")}
                onChange={(e) => {
                  const langs = e.target.value.split(",").map((val) => ({
                    name: val.trim(),
                    proficiency: "Fluent",
                    read: true,
                    write: true,
                    speak: true,
                  })).filter((l) => l.name);
                  setFormData({ ...formData, languages: langs });
                }}
                placeholder="English, Hindi, Telugu"
              />
            </div>

            {/* Save Buttons */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
